import { IAppRestClient } from "./i-app-rest.client";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
  ARestClient,
  ICreateRecordResponse,
  IImageUploadResponse,
  IOkResponse,
  IPage,
  ISort
} from "@zaeper/communication-lib";
import { ICreateAppForm } from "./forms/i-create-app.form";
import { IUpdateAppForm } from "./forms/i-update-app.form";
import { IGetAppPageForm } from "./forms/i-get-app-page.form";
import { HostUtil } from "../../../utils/host-util";
import { firstValueFrom, Observable } from "rxjs";
import { IApp } from "../../../models/application/i-app";
import { IGetAppForm } from "./forms/i-get-app.form";
import { IUploadTitleImageForm } from "./forms/i-upload-title-image-form";
import { IUploadIconImageForm } from "./forms/i-upload-icon-image-form";
import { IUploadLogoImageForm } from "./forms/i-upload-logo-image-form";

@Injectable()
export class AppRestClient extends ARestClient implements IAppRestClient {
  constructor(private readonly _httpClient: HttpClient) {
    super();
  }

  createApp(createAppForm: ICreateAppForm): Promise<ICreateRecordResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), "v1/app" );

    return firstValueFrom( this._httpClient.post( endpointUrl, createAppForm, { withCredentials: true } ) as Observable<ICreateRecordResponse> );
  }

  updateApp(
    recordId: string,
    updateAppForm: IUpdateAppForm
  ): Promise<IOkResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/app/${ recordId }` );

    return firstValueFrom( this._httpClient.put( endpointUrl, updateAppForm, { withCredentials: true } ) as Observable<IOkResponse> );
  }

  deleteApp(recordId: string): Promise<IOkResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/app/${ recordId }` );

    return firstValueFrom( this._httpClient.delete( endpointUrl, { withCredentials: true } ) as Observable<IOkResponse> );
  }

  getApp(getAppForm: IGetAppForm): Promise<IApp> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/app/${ getAppForm.recordId }` );

    return firstValueFrom( this._httpClient.get( endpointUrl, {
      withCredentials: true,
    } ) as Observable<IApp> );
  }

  uploadLogoImage(
    recordId: string,
    uploadLogoImageForm: IUploadLogoImageForm
  ): Promise<IImageUploadResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/app/${ recordId }/logo-image` );

    const formData: FormData = new FormData();
    formData.set( "logoImage", uploadLogoImageForm.logoImage );
    formData.set( "logoType", uploadLogoImageForm.logoType );

    return firstValueFrom( this._httpClient.post( endpointUrl, formData, {
      withCredentials: true,
    } ) as Observable<IImageUploadResponse> );
  }


  uploadIconImage(
    recordId: string,
    uploadIconImageForm: IUploadIconImageForm
  ): Promise<IImageUploadResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/app/${ recordId }/icon-image` );

    const formData: FormData = new FormData();
    formData.set( "iconImage", uploadIconImageForm.iconImage );
    formData.set( "iconType", uploadIconImageForm.iconType );

    return firstValueFrom( this._httpClient.post( endpointUrl, formData, {
      withCredentials: true,
    } ) as Observable<IImageUploadResponse> );
  }

  uploadTitleImage(
    recordId: string,
    uploadTitleImageForm: IUploadTitleImageForm
  ): Promise<IImageUploadResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/app/${ recordId }/title-image` );

    const formData: FormData = new FormData();
    formData.set( "titleImage", uploadTitleImageForm.titleImage );

    return firstValueFrom( this._httpClient.post( endpointUrl, formData, {
      withCredentials: true,
    } ) as Observable<IImageUploadResponse> );
  }

  publishApp(recordId: string): Promise<IOkResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/app/${ recordId }/publish` );

    return firstValueFrom( this._httpClient.post( endpointUrl, {}, { withCredentials: true } ) as Observable<IOkResponse> );
  }

  getAppPage(getAppPageForm: IGetAppPageForm): Promise<IPage<IApp>> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), "v1/app/page" );

    const defaultSort: ISort = {
      field: "name",
      ascending: true
    };

    return firstValueFrom( this._httpClient.get( endpointUrl, {
      withCredentials: true,
      params: {
        pageNumber: getAppPageForm.pageNumber,
        pageSize: getAppPageForm.pageSize,
        sort: super.serializeSort( defaultSort )
      }
    } ) as Observable<IPage<IApp>> );
  }
}
